import Column from '@amzn/meridian/column';
import styled from '@emotion/styled';
import React, { FC, useMemo } from 'react';
import { useItemTableHelpers } from '../../hooks/useItemTableHelpers';
import { DetailedClaim, IAsynchronouslyLoadedValue, OfflinePromotion, ResellerItem } from '../../redux/types';
import { color } from '../../theme/colors';
import { formatCurrency } from '../../utils/currencyUtils';
import { useTranslation } from '../blocks';
import { ColumnFormat, SuperPaginatedTable, SuperPaginatedTableColumn } from '../constructed/SuperPaginatedTable';

export type ClaimLineItemListProps = {
    claimDetails: DetailedClaim;
    itemMap: Map<string, ResellerItem>;
    isPromotional?: boolean;
    promotionMap?: Map<string, OfflinePromotion>;
    filteredData?: any[];
    children?: React.ReactNode;
    asinToImageUrls: IAsynchronouslyLoadedValue<Map<string, string>>;
};

const BorderedColumn = styled(Column)`
    border: 1px solid ${color.gray[300]};
    box-shadow: 0px 0px 3px ${color.gray[300]};
`;

export const ClaimLineItemList: FC<ClaimLineItemListProps> = ({
    claimDetails,
    itemMap,
    isPromotional = false,
    promotionMap,
    filteredData,
    children,
    asinToImageUrls,
}) => {
    const { t } = useTranslation('claimDetails');
    const { t: tCommon } = useTranslation('common');

    const { titleFormat, imageFormat, promoSkuFormat, upcFormat, asinFormat } = useItemTableHelpers(
        itemMap,
        asinToImageUrls
    );

    const unitsClaimedFormat: ColumnFormat = useMemo(
        () => ({
            render: (unitsClaimed: number) => unitsClaimed.toLocaleString(),
            sort: (unitsClaimed: number) => String(unitsClaimed).padStart(10),
        }),
        []
    );

    const currencyFormat: ColumnFormat = useMemo(
        () => ({
            render: (amount: number) => formatCurrency(amount, claimDetails?.currency || ''),
            sort: (amount: number) => (amount || 0).toFixed(8).padStart(20, '0'),
        }),
        [claimDetails.currency]
    );

    const baseColumns: SuperPaginatedTableColumn[] = [
        {
            sourceProperty: 'itemId',
            id: 'sku',
            title: t('columnHeader-sku'),
            format: promoSkuFormat,
        },
        {
            sourceProperty: 'itemId',
            id: 'upc',
            title: t('columnHeader-upc'),
            format: upcFormat,
        },
        {
            sourceProperty: 'itemId',
            id: 'asin',
            title: t('columnHeader-asin'),
            format: asinFormat,
        },
        {
            sourceProperty: 'itemId',
            id: 'item',
            title: t('columnHeader-item'),
            format: titleFormat,
            maxWidth: 100,
        },
        {
            sourceProperty: 'itemId',
            id: 'image',
            title: t.cognate(' '),
            format: imageFormat,
            searchable: false,
        },
    ];

    const claimLineItemMap = useMemo(
        () => new Map(claimDetails.claimLineItems.map((item) => [item.claimLineItemId, item])),
        [claimDetails.claimLineItems]
    );

    const promotionFormat: ColumnFormat = useMemo(() => {
        const getText = (claimLineItemId: string) => {
            const item = claimLineItemMap.get(claimLineItemId);
            return item?.promotionId || 'N/A';
        };
        return {
            render: getText,
            sort: getText,
            search: getText,
        };
    }, [claimLineItemMap]);

    const promotionDescriptionFormat: ColumnFormat = useMemo(() => {
        const getText = (claimLineItemId: string) => {
            const promotionId = claimLineItemMap.get(claimLineItemId)?.promotionId;

            if (promotionId) return promotionMap?.get(promotionId)?.description || tCommon('not-available-text');
            return tCommon('not-available-text');
        };
        return {
            render: getText,
            sort: getText,
            search: getText,
        };
    }, [claimLineItemMap, promotionMap, tCommon]);

    const fundingSplitFormat: ColumnFormat = useMemo(() => {
        const getText = (claimLineItemId: string) => {
            const supplemental = claimDetails?.supplementalLineItems[claimLineItemId];
            return supplemental?.fundingSplitValue ? `${(supplemental.fundingSplitValue * 100).toFixed(1)}%` : 'N/A';
        };

        return {
            render: getText,
            sort: (claimLineItemId: string) => {
                const supplemental = claimDetails?.supplementalLineItems[claimLineItemId];
                return supplemental?.fundingSplitValue?.toString() || '0';
            },
            search: getText,
        };
    }, [claimDetails.supplementalLineItems]);

    const columns = useMemo(() => {
        if (isPromotional) {
            return [
                ...baseColumns,
                {
                    sourceProperty: 'claimLineItemId',
                    id: 'promotion',
                    title: t('columnHeader-promotion'),
                    format: promotionFormat,
                },
                {
                    sourceProperty: 'claimLineItemId',
                    id: 'promotionDescription',
                    title: t('columnHeader-promotionDescription'),
                    format: promotionDescriptionFormat,
                },
                {
                    sourceProperty: 'claimLineItemId',
                    id: 'fundingSplit',
                    title: t('columnHeader-fundingSplit'),
                    format: fundingSplitFormat,
                    searchable: false,
                },
                {
                    sourceProperty: 'fundingAmount',
                    title: t('columnHeader-fundingAmount'),
                    format: currencyFormat,
                    searchable: false,
                },
                {
                    sourceProperty: 'unitsClaimed',
                    title: t('columnHeader-unitsClaimed'),
                    format: unitsClaimedFormat,
                    searchable: false,
                },
                {
                    sourceProperty: 'claimAmount',
                    title: t('columnHeader-claimAmount'),
                    format: currencyFormat,
                    searchable: false,
                },
            ];
        } else {
            return [
                ...baseColumns,
                {
                    sourceProperty: 'unitsClaimed',
                    title: t('columnHeader-unitsClaimed'),
                    format: unitsClaimedFormat,
                    searchable: false,
                },
                {
                    sourceProperty: 'claimAmount',
                    title: t('columnHeader-claimAmount'),
                    format: currencyFormat,
                    searchable: false,
                },
                {
                    sourceProperty: 'claimAmountOverride',
                    title: t('columnHeader-claimAmountOverride'),
                    format: currencyFormat,
                    searchable: false,
                },
            ];
        }
    }, [
        isPromotional,
        baseColumns,
        t,
        promotionFormat,
        promotionDescriptionFormat,
        fundingSplitFormat,
        currencyFormat,
        unitsClaimedFormat,
    ]);

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <SuperPaginatedTable
                data={filteredData || claimDetails.claimLineItems}
                layout={{
                    rowKey: 'claimLineItemId',
                    pageSize: 10,
                    searchPlaceholder: t('findResources-searchBoxPlaceholder'),
                    columns,
                }}
            >
                {children}
            </SuperPaginatedTable>
        </BorderedColumn>
    );
};
